/**
 * Consulta GLN ANMAT — busca un GLN en vivo contra el SNT (ANMAT) vía riga.
 * Solo lectura: no guarda nada. Muestra el agente registrado (razón social,
 * CUIT, tipo de agente, domicilio).
 */

import { useState } from 'react';
import { Search, Loader2, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useGlnSnt } from '@/api/queries';
import type { GlnSntAgente } from '@/api/endpoints/snt';

export function ConsultaGlnPage() {
  const [input, setInput] = useState('');
  const [buscado, setBuscado] = useState('');

  const { data, isLoading, error, isFetching } = useGlnSnt(buscado || null);

  const onBuscar = (e: React.FormEvent) => {
    e.preventDefault();
    setBuscado(input.trim());
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4">
          <p className="mb-3 text-xs text-gray-600">
            Consulta en vivo de un GLN contra el SNT (ANMAT). No se guarda nada.
          </p>
          <form onSubmit={onBuscar} className="flex flex-wrap items-end gap-2">
            <div className="relative min-w-[260px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value.replace(/\D/g, '').slice(0, 13))}
                inputMode="numeric"
                placeholder="GLN (ej. 7793236000007)"
                className="pl-9 font-mono"
              />
            </div>
            <Button
              type="submit"
              disabled={isFetching || input.trim() === ''}
              className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
            >
              {isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              <span className="ml-1.5">Consultar</span>
            </Button>
          </form>
        </CardContent>
      </Card>

      {!buscado ? (
        <Card>
          <CardContent className="py-8 text-center text-xs text-gray-500">
            Ingresá un GLN y presioná <b>Consultar</b> para traerlo del SNT.
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-600">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          <span>Consultando en SNT…</span>
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertDescription className="text-xs">
            No se pudo consultar el SNT:{' '}
            {error instanceof Error ? error.message : String(error)}
          </AlertDescription>
        </Alert>
      ) : data ? (
        <AgenteCard agente={data} />
      ) : (
        <Alert>
          <AlertDescription className="flex items-center gap-2 text-xs text-gray-600">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            El GLN <span className="font-mono">{buscado}</span> no está registrado
            en el SNT.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

function AgenteCard({ agente }: { agente: GlnSntAgente }) {
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="mb-4 flex flex-wrap items-baseline gap-3 border-b border-gray-100 pb-3">
          <span className="rounded bg-everex-azul/10 px-2 py-1 font-mono text-sm font-bold text-everex-azul">
            {agente.gln}
          </span>
          <span className="text-base font-semibold text-gray-800">
            {agente.razonSocial ?? '—'}
          </span>
          {agente.tipoAgente && (
            <span className="rounded bg-green-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-green-800">
              {agente.tipoAgente}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs md:grid-cols-3">
          <Dato label="Tipo agente SNT">{agente.tipoAgente ?? '—'}</Dato>
          <Dato label="CUIT" mono>
            {agente.cuit ?? '—'}
          </Dato>
          <Dato label="Razón social">{agente.razonSocial ?? '—'}</Dato>
          <Dato label="Dirección">
            {[agente.direccion, agente.numero].filter(Boolean).join(' ') || '—'}
          </Dato>
          <Dato label="Localidad">{agente.localidad ?? '—'}</Dato>
          <Dato label="Provincia">{agente.provincia ?? '—'}</Dato>
          <Dato label="Código postal" mono>
            {agente.codigoPostal ?? '—'}
          </Dato>
          <Dato label="Teléfono" mono>
            {agente.telefono ?? '—'}
          </Dato>
          <Dato label="Email">{agente.email ?? '—'}</Dato>
        </div>
      </CardContent>
    </Card>
  );
}

function Dato({
  label,
  mono = false,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className={cn('mt-0.5 text-gray-800', mono && 'font-mono')}>{children}</div>
    </div>
  );
}
